import { HeaderModel, HeaderNavigationItemModel, HeaderNavigationModel, LinkModel } from '@/types';
import ResponsiveImage from '../general/ResponsiveImage';
import Link from '../general/Link';
import classNames from 'classnames';
import DesktopNavApp from './DesktopNavApp';
import { SystemFeaturesContext, Tooltip } from '..';
import AI from '@/assets/ai.svg';
import UserNavigation from './UserNavigation';
import HeaderSearch from './HeaderSearch';
import MobileNavApp from './MobileNavApp/MobileNavApp';
import { useCallback, useContext, useMemo } from 'react';

interface IHeaderAppModel {
  data?: HeaderModel;
}

const HeaderApp = ({ data, ...props }: IHeaderAppModel) => {
  const { aiChatPage, scrapbookPage, techLaunchesDatabase, reportGenerator, socialMediaDashboard, pldb } =
    useContext(SystemFeaturesContext);

  const lockedLinks = useMemo(() => {
    const locked: string[] = [];
    if (!aiChatPage) locked.push('/tools/stylusai');
    if (!scrapbookPage) locked.push('/tools/scrapbook');
    if (!techLaunchesDatabase) locked.push('/tools/tech-launches-database');
    if (!reportGenerator) locked.push('/tools/report-generator');
    if (!socialMediaDashboard) locked.push('/tools/consumer-signals-dashboard');
    if (!pldb) locked.push('/tools/product-lifecycle-database');
    return locked;
  }, [aiChatPage, scrapbookPage, techLaunchesDatabase, reportGenerator, socialMediaDashboard, pldb]);

  const processNavigationItems = useCallback(
    (items: HeaderNavigationItemModel[]) => {
      return items.map((item) => {
        if (item.props.navigationItems) {
          const navigationItems = item.props.navigationItems;
          navigationItems.forEach((navItem) => {
            if (lockedLinks.includes(navItem.link.href)) {
              navItem.locked = true;
            }
          });

          return {
            ...item,
            props: {
              ...item.props,
              navigationItems,
            },
          };
        }
        return item;
      });
    },
    [lockedLinks],
  );

  const updatedLinks: HeaderNavigationModel[] = useMemo(() => {
    return (
      data?.links.map((link) => ({
        ...link,
        props: {
          ...link.props,
          column1NavigationItems: processNavigationItems(link.props.column1NavigationItems || []),
          column2NavigationItems: processNavigationItems(link.props.column2NavigationItems || []),
          column3NavigationItems: processNavigationItems(link.props.column3NavigationItems || []),
        },
      })) || []
    );
  }, [data?.links, processNavigationItems]);

  if (!data) return null;

  const iconLinks: { icon: React.ReactNode; link: LinkModel }[] = [
    {
      icon: <AI />,
      link: { href: '/tools/stylusai', text: 'StylusAI' },
    },

    ...(data.iconLinks?.map((item) => ({
      icon: <ResponsiveImage image={item.icon} widths={{ xs: 24 }} heights={{ xs: 24 }} dataNotLazy preload />,
      link: item.link,
    })) || []),
  ];

  return (
    <header
      className={classNames(
        'main-header group border-gray-2 fixed top-0 right-0 left-0 z-50 h-[var(--header-height)] border-b transition-colors duration-500 ease-out',
        'print:relative',
        'solid-bg bg-white',
      )}
      {...props}
    >
      <div className="container flex h-full items-center justify-between lg:px-2">
        <div className="flex h-full items-center gap-3">
          <Link link={{ href: '/' }} className="flex h-full items-center justify-center outline-none">
            {data.logos && data.logos.length >= 1 && data.logos[0]?.logoImage && (
              <div className="mr-12 w-[115px]">
                <ResponsiveImage
                  widths={{ xs: null }}
                  image={data.logos[0]?.logoImage}
                  dataNotLazy
                  className={classNames(
                    'filter-black group-[.transparent-bg]:filter-white transition-all duration-500',
                  )}
                />
              </div>
            )}
          </Link>

          <HeaderSearch className="hidden lg:block" />
        </div>

        <DesktopNavApp links={updatedLinks} className="hidden lg:flex print:hidden" />
        <MobileNavApp links={updatedLinks} data={data} className="lg:hidden print:hidden" iconLinks={iconLinks} />

        <div className="hidden h-8/12 lg:flex">
          {iconLinks.map((link, index) => (
            <Tooltip key={index} placement="bottom" content={link.link.text || ''}>
              <Link link={link.link} className="flex h-full w-10 items-center justify-center">
                <div className="h-6 w-6">{link.icon}</div>
              </Link>
            </Tooltip>
          ))}
          <UserNavigation links={data.userNavigationLinks} notificationsCount={data.notificationsCount} />
        </div>
      </div>
    </header>
  );
};

export default HeaderApp;
